import { collection } from 'firebase/firestore';
import { firestore } from '@api/Firebase/Firebase';
import { ProfileTypes, UserType } from '@api/Users/User';
import { FirestoreDateLike, toMillisSafe } from '@utils/Date';

/**
 * Notificaciones internas del staff.
 *
 * **La notificación es el DATO; el push es TRANSPORTE** (decisión de Irving,
 * 2026-08-29). El aviso se guarda SIEMPRE aquí, y el web push solo intenta
 * alcanzar a la persona en el momento: si el push no llegó —permiso denegado,
 * tablet apagada, navegador cerrado— el aviso sigue en su campana. Ésa es la
 * diferencia con el WhatsApp, donde un envío fallido se perdía para siempre en
 * un `console.error` que nadie leía.
 *
 * Nace de dos decisiones del mismo día: por WhatsApp sale **un solo mensaje por
 * pedido** (cuesta por envío) y **la cuota del pase de Wallet se reserva** para
 * cercanía y puntos. Todo lo demás vive aquí.
 */

export enum NotificationType {
    Order = 'order',
    CashRegister = 'cash-register',
    Inventory = 'inventory',
    System = 'system'
}

/**
 * A quién va el aviso. Los criterios presentes se cumplen TODOS (AND) y dentro
 * de cada uno basta con coincidir en uno (OR). Un criterio ausente no filtra:
 * una notificación sin audiencia la ve todo el staff.
 *
 * ⚠️ Se resuelve **en el cliente**, no en la query: Firestore no sabe hacer OR
 * entre campos distintos sin índices compuestos, y aquí no se usan (regla del
 * repo). El volumen lo permite — son avisos de días recientes, no un histórico.
 */
export interface NotificationAudience {
    profiles?: ProfileTypes[];
    branchOfficeIds?: string[];
    userIds?: string[];
}

export interface AppNotification {
    id?: string;
    title: string;
    body?: string;
    type: NotificationType;
    audience?: NotificationAudience;
    /** Ruta interna a la que lleva al tocarla, ej. `/admin/orders`. */
    link?: string;
    /**
     * Llave de idempotencia — mismo patrón que los puntos y el ledger de
     * inventario. Sin esto, "el pedido 42 lleva 15 minutos" se notificaría en
     * cada evaluación.
     */
    referenceId?: string;
    createdAt: any;
    /** uids que ya la leyeron. Crece poco: el staff son 42 personas. */
    readBy?: string[];
}

export const NOTIFICATIONS_COLLECTION = 'notifications';
export const notificationsRef = collection(firestore, NOTIFICATIONS_COLLECTION);

/**
 * Cuántos días de notificaciones se traen y se conservan.
 *
 * ⚠️ **La retención se decide DESDE EL PRIMER DÍA**, a propósito: `activity-logs`
 * lleva 177 mil documentos por no haberlo hecho, y en Firestore cada lectura se
 * paga (regla del CLAUDE.md raíz). Este número acota la query de la campana.
 */
export const NOTIFICATIONS_RETENTION_DAYS = 30;

/** Tope de la consulta: la campana muestra lo reciente, no un archivo. */
export const NOTIFICATIONS_QUERY_LIMIT = 50;

/**
 * Dónde se guarda hasta dónde leyó cada persona: **UN documento por usuario**.
 *
 * ⚠️ Antes "leído" era un `readBy: uid[]` DENTRO de cada aviso, así que abrir la
 * campana escribía hasta 50 documentos — y como la consulta de la campana no
 * filtra por sucursal (no puede: sería un índice compuesto, y este repo no los
 * mantiene), **cada una de esas escrituras se le re-entregaba a TODAS las
 * campanas abiertas de las nueve sucursales**. Con ~25 dispositivos, una sola
 * apertura costaba del orden de 1,250 lecturas ajenas.
 *
 * Ahora es **una escritura** en un documento que NADIE escucha. Es el mismo
 * patrón que ya usaba la campana del cliente (`customer-notifications`:
 * `readUpTo` en vez de una marca por elemento).
 */
export const NOTIFICATION_READS_COLLECTION = 'notification-reads';
export const notificationReadsRef = collection(firestore, NOTIFICATION_READS_COLLECTION);

/**
 * Milisegundos de `createdAt`. Tuvo su propia copia mientras `parseFirebaseDate`
 * estuvo roto; desde el 2026-09-11 delega en `toMillisSafe` (`@utils/Date`), que
 * es la única implementación. Se conserva la función porque el contrato de aquí
 * es el de las notificaciones (`0` = sin fecha) y lo usan el orden y el badge.
 */
export const getNotificationMillis = (notification?: AppNotification): number => {
    return toMillisSafe(notification?.createdAt as FirestoreDateLike);
};

/** Milisegundos de una marca `readUpTo` (mismas tres formas). */
export const getReadUpToMillis = (readUpTo?: unknown): number => getReadUpToMillisFrom(readUpTo);

const getReadUpToMillisFrom = (value?: unknown): number =>
    getNotificationMillis({ createdAt: value } as unknown as AppNotification);

/** ¿Este usuario es destinatario? Puro, para poder probarlo sin Firestore. */
export const isNotificationForUser = (
    notification: AppNotification,
    user: Pick<UserType, 'uid'> & { profile?: { profile?: ProfileTypes; branchOffices?: string[] } },
    branchOfficeId?: string
): boolean => {
    const audience = notification.audience;
    if (!audience) return true;

    if (audience.userIds?.length) {
        if (!user.uid || !audience.userIds.includes(user.uid)) return false;
    }
    if (audience.profiles?.length) {
        const profile = user.profile?.profile;
        if (!profile || !audience.profiles.includes(profile)) return false;
    }
    if (audience.branchOfficeIds?.length) {
        // ⚠️ **La sucursal EN OPERACIÓN manda sobre todo, incluso para quien
        // tiene 'all'** (corregido el 2026-08-29, reportado por Irving).
        //
        // Antes un usuario con acceso a todas las sucursales recibía los avisos
        // de las nueve, estuviera donde estuviera. El síntoma que lo delató: al
        // tocar el aviso aterrizaba en una lista VACÍA, porque la pantalla de
        // pedidos filtra por la sucursal en operación y el aviso era de otra.
        //
        // La regla correcta es "a quien está ahí atendiendo": tener acceso a una
        // sucursal no es lo mismo que estar en ella. Quien quiera ver las otras,
        // cambia de sucursal en el sidebar.
        if (branchOfficeId) {
            return audience.branchOfficeIds.includes(branchOfficeId);
        }
        // Sin sucursal en operación (sesión recién abierta) se cae a las
        // asignadas: es mejor mostrar de más que dejar a alguien sin avisos.
        const asignadas = user.profile?.branchOffices || [];
        if (asignadas.includes('all')) return true;
        if (!asignadas.some((id) => audience.branchOfficeIds?.includes(id))) return false;
    }
    return true;
};

/**
 * ¿Este usuario ya lo leyó?
 *
 * Dos vías, y las dos hacen falta: la marca `readUpTo` del usuario (la de hoy,
 * una escritura por apertura) y el `readBy` dentro del aviso (**el histórico**
 * — sin él, todo lo que ya se había marcado antes del 2026-09-09 volvería a
 * aparecer sin leer de golpe).
 */
export const isNotificationRead = (notification: AppNotification, uid?: string, readUpTo?: unknown): boolean => {
    if (!uid) return false;
    if (notification.readBy?.includes(uid)) return true;
    const marca = getReadUpToMillis(readUpTo);
    if (!marca) return false;
    const creado = getNotificationMillis(notification);
    return creado > 0 && creado <= marca;
};

/** Las visibles para el usuario, más recientes primero. */
export const filterNotificationsForUser = (
    notifications: AppNotification[],
    user: Parameters<typeof isNotificationForUser>[1],
    branchOfficeId?: string
): AppNotification[] => notifications.filter((n) => isNotificationForUser(n, user, branchOfficeId));

export const countUnread = (notifications: AppNotification[], uid?: string, readUpTo?: unknown): number =>
    notifications.filter((n) => !isNotificationRead(n, uid, readUpTo)).length;

/**
 * Convierte el `link` de un aviso en una ruta que el router sí conoce.
 *
 * ⚠️ **Las rutas del admin viven bajo `/{profileSelected}/…`**, así que un
 * `/orders` pelado NO existe: cae en el catch-all del router, que redirige al
 * home — y el home del admin es el Dashboard. Ése era el motivo de que todas las
 * notificaciones aterrizaran ahí sin importar a dónde apuntaran (2026-08-29).
 *
 * El link se guarda **sin perfil** a propósito: el mismo aviso lo pueden recibir
 * un admin y un gerente, y cada uno tiene su propio prefijo. Un link que ya
 * traiga el perfil, o que sea externo, se respeta tal cual.
 */
export const resolveNotificationLink = (link: string, profileSelected?: string): string => {
    if (!link) return '/';
    if (/^https?:\/\//i.test(link)) return link;
    const limpio = link.startsWith('/') ? link : `/${link}`;
    if (!profileSelected) return limpio;
    if (limpio === `/${profileSelected}` || limpio.startsWith(`/${profileSelected}/`)) return limpio;
    return `/${profileSelected}${limpio}`;
    // Ojo: el query string viaja dentro de `link` (ej. `/dashboard?tab=cashRegisters`)
    // y se conserva tal cual — el prefijo va DELANTE, nunca en medio.
};
